import { useCallback, useContext, useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import NonIdealNew from "../../../../common/NonIdealNew";
import { ParagraphText } from "../../../../../Libraries";
import ReportPageHeader from "../ReportPageHeader";
import { FilterNonIdeal } from "../../../../common";
import pageStyles from "../styles.module.css";
import styles from "./SalesByPaymentMethod.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";
import { useReports } from "../../../../../hooks/useReports";
import { useAuthContext } from "../../../../../context/AuthContext";
import { formatReportCurrency, getStartAndEndDate, REPORT_DATE_FILTERS } from "../../../../../utils";
import dayjs from "dayjs";
import useReportExport from "../../../../../hooks/useReportExport";
import useReportFilter from "../../../../../hooks/useReportFilter";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";

const SalesByPaymentMethod = () => {
  const [I18n] = useContext(LanguageContext);
  const { handleFetchReport, loading, salesByPaymentMethod, salesByPaymentMethodTotal } = useReports(I18n);
  const { business } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReport = useCallback((start, end) => {
    handleFetchReport({
      business_id: business?.id,
      report_type: "sales_by_payment_method",
      start_date: dayjs(start?.toDate ? start.toDate() : start).toISOString(),
      end_date: dayjs(end?.toDate ? end.toDate() : end).toISOString(),
    });
  }, [handleFetchReport, business?.id]);


  useEffect(() => {
    triggerLogEvent(analytics, "report_sales_by_payment_method_visited", {});
  }, []);

  const { handleExport, exportLoading } = useReportExport("sales_by_payment_method");
  const { headerDefaultFilter, isYearlyFilter, resetCount, handleFilterDate, handleResetFilter } = useReportFilter(fetchReport, "sales_by_payment_method");

  const showNonIdeal = isYearlyFilter && !loading && salesByPaymentMethod.length === 0;

  // Server sends English method names; show the translated label when one exists
  const getMethodLabel = (method) =>
    I18n[String(method || "").toLowerCase()] || method;

  const minDate = useMemo(() => dayjs().subtract(1, "year"), []);
  const maxDate = useMemo(() => dayjs().endOf("day"), []);

  return (
    <Box id="sales_by_payment_page_wrapper" className={pageStyles.pageWrapper}>
      <ReportPageHeader
        title={I18n.sales_by_payment_method}
        id="sales_by_payment"
        dateFilters={REPORT_DATE_FILTERS}
        onFilterDate={handleFilterDate}
        minDate={minDate}
        maxDate={maxDate}
        defaultFilter={headerDefaultFilter}
        resetKey={resetCount}
        hideActions={showNonIdeal}
        onExport={handleExport}
        exportLoading={exportLoading}
      />

      {showNonIdeal ? (
        <Box id="sales_by_payment_nonideal_body" className={pageStyles.pageBody}>
          <NonIdealNew
            buttonOneId="sales_by_payment_nonideal_start_sale_btn"
            title={I18n.no_data_yet}
            subtitle={I18n.it_looks_like_you_dont_have_any_data_yet}
            button1Title={I18n.start_new_sale}
            onButton1Click={() => {
              triggerLogEvent(analytics, "report_nonideal_start_new_sale", { report_type: "sales_by_payment_method" });
              navigate("/QuickSale/QuickSaleForm", { state: { pathname: location.pathname } });
            }}
            loading={false}
            disabledOne={false}
          />
        </Box>
      ) : (
        <>
          {(loading || salesByPaymentMethod.length > 0) && (
          <Box id="sales_by_payment_chart_body" className={styles.body}>
            {loading ? (
              <>
                <Skeleton variant="rounded" animation="wave" width={100} height={14} sx={{ borderRadius: "6px", mb: "10px" }} />
                <Skeleton variant="rounded" animation="wave" width={130} height={22} sx={{ borderRadius: "6px", mb: "16px" }} />
                <Box className={styles.barsRow}>
                  {[65, 35].map((w, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      animation="wave"
                      height={40}
                      sx={{ borderRadius: "8px", width: `${w}%`, flexShrink: 0 }}
                    />
                  ))}
                </Box>
                <Box className={styles.labelsRow}>
                  {[{ w: 65, lw: 88 }, { w: 35, lw: 72 }].map(({ w, lw }, i) => (
                    <Box key={i} style={{ width: `100px` }}>
                      <Skeleton variant="rounded" animation="wave" width={lw} height={14} sx={{ borderRadius: "6px" }} />
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <>
                <ParagraphText id="sales_by_payment_total_label" className={styles.totalLabel}>
                  {I18n.total_collected}
                </ParagraphText>
                <ParagraphText id="sales_by_payment_total_amount" className={styles.totalAmount}>
                  {formatReportCurrency(salesByPaymentMethodTotal)}
                </ParagraphText>

                <Box id="sales_by_payment_bars_row" className={styles.barsRow}>
                  {salesByPaymentMethod.filter(({ percentage }) => percentage > 0).map(({ method, percentage, barColor }) => (
                    <Box
                      key={method}
                      id={`sales_by_payment_bar_${method}`}
                      className={styles.barSegment}
                      style={{ width: `${percentage}%`, background: barColor }}
                    />
                  ))}
                </Box>

                <Box id="sales_by_payment_labels_row" className={styles.labelsRow}>
                  {salesByPaymentMethod
                    .filter(({ percentage }) => percentage > 0)
                    .map(({ method, percentage, barColor }) => (
                      <Box
                        key={method}
                        id={`sales_by_payment_bar_label_box_${method}`}
                        className={styles.barLabelChip}
                      >
                        <Box className={styles.barLabelDot} style={{ background: barColor }} />
                        <ParagraphText
                          id={`sales_by_payment_bar_label_${method}`}
                          className={styles.barLabel}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {getMethodLabel(method)} ({percentage}%)
                        </ParagraphText>
                      </Box>
                    ))}
                </Box>
              </>
            )}
          </Box>
          )}

          <Box id="sales_by_payment_table_wrapper" className={styles.tableWrapper}>
            {(loading || salesByPaymentMethod.length > 0) && (
              <Box id="sales_by_payment_table_header" className="invoiceListTableHeader">
                <Box id="sales_by_payment_header_method" className="invListTableHeaderCol fourColumn textLeft">
                  <ParagraphText id="sales_by_payment_header_label_method" className="fontSize12 fontWeight500 newLightColor">
                    {I18n.payment_method}
                  </ParagraphText>
                </Box>
                <Box id="sales_by_payment_header_transactions" className="invListTableHeaderCol fourColumn textLeft">
                  <ParagraphText id="sales_by_payment_header_label_transactions" className="fontSize12 fontWeight500 newLightColor">
                    {I18n.orders}
                  </ParagraphText>
                </Box>
                <Box id="sales_by_payment_header_percentage" className="invListTableHeaderCol fourColumn textLeft">
                  <ParagraphText id="sales_by_payment_header_label_percentage" className="fontSize12 fontWeight500 newLightColor">
                    {I18n.percentage}
                  </ParagraphText>
                </Box>
                <Box id="sales_by_payment_header_total_sales" className="invListTableHeaderCol fourColumn textRight">
                  <ParagraphText id="sales_by_payment_header_label_total_sales" className="fontSize12 fontWeight500 newLightColor">
                    {I18n.total_sales}
                  </ParagraphText>
                </Box>
              </Box>
            )}

            {loading
              ? Array.from({ length: 2 }).map((_, i) => (
                  <Box key={i} className="invoiceListTableBody defaultCursorImportant">
                    <Box className="invListTableHeaderCol fourColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={60} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol fourColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={30} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol fourColumn textLeft">
                      <Skeleton variant="rounded" animation="wave" width={40} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                    <Box className="invListTableHeaderCol fourColumn textRight" sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" animation="wave" width={80} height={16} sx={{ borderRadius: "6px" }} />
                    </Box>
                  </Box>
                ))
              : salesByPaymentMethod.length === 0
                ? (
                    <FilterNonIdeal
                      title={I18n.no_transactions_matching_those_filters}
                      subTitle={`${I18n.try_adjusting_filters} `}
                      subTitleText={I18n.view_yearly_data}
                      subTitleTextOnClick={handleResetFilter}
                      subTitleTextId="sales_by_payment_filter_view_yearly"
                    />
                  )
                : salesByPaymentMethod.map(({ method, transactions, percentage, total_sales }, i) => (
                    <Box key={method} id={`sales_by_payment_row_${i}`} className="invoiceListTableBody defaultCursorImportant">
                      <Box id={`sales_by_payment_row_${i}_method`} className="invListTableHeaderCol fourColumn textLeft">
                        <ParagraphText id={`sales_by_payment_cell_${i}_method`} className="fontSize14 newFontColor">{getMethodLabel(method)}</ParagraphText>
                      </Box>
                      <Box id={`sales_by_payment_row_${i}_transactions`} className="invListTableHeaderCol fourColumn textLeft">
                        <ParagraphText id={`sales_by_payment_cell_${i}_transactions`} className="fontSize14 newFontColor">{transactions}</ParagraphText>
                      </Box>
                      <Box id={`sales_by_payment_row_${i}_percentage`} className="invListTableHeaderCol fourColumn textLeft">
                        <ParagraphText id={`sales_by_payment_cell_${i}_percentage`} className="fontSize14 newFontColor">{percentage}%</ParagraphText>
                      </Box>
                      <Box id={`sales_by_payment_row_${i}_total_sales`} className="invListTableHeaderCol fourColumn textRight">
                        <ParagraphText id={`sales_by_payment_cell_${i}_total_sales`} className="fontSize14 newFontColor">{formatReportCurrency(total_sales)}</ParagraphText>
                      </Box>
                    </Box>
                  ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default SalesByPaymentMethod;
